import { useState } from "react";
import { Mic, NotebookPen, TriangleAlert, Users } from "lucide-react";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { GlanceStat } from "@/components/gb/stat-card";
import { SectionLabel } from "@/components/gb/section-label";
import { Button } from "@/components/ui/button";
import { JournalComposer } from "@/features/journal/journal-composer";
// One definition of "today" for the whole journal, so the card and the
// calendar's Today fill cannot land on different dates.
import { todayIso } from "@/features/journal/journal-calendar";
import { firstName, greetingFor, longDateLabel } from "@/lib/format";
import {
  useComplianceAlerts,
  useCrew,
  useProjectName,
  useSiteManager,
} from "@/hooks/use-site";
import { ComplianceBanner } from "./compliance-banner";
import { ConnectionStatus } from "./connection-status";
import { SiteBrainHero } from "./site-brain-hero";

export function TodayScreen() {
  const { projectId } = useProjectContext();
  const projectName = useProjectName();
  const siteManager = useSiteManager();
  const { onSite } = useCrew();
  const alerts = useComplianceAlerts();

  // The capture card's own state. The composer is a modal rather than a route
  // (JRNL-07), so it opens over whatever screen invoked it — this one, and the
  // Journal, and the catch-up queue, all the same component.
  const [logging, setLogging] = useState(false);

  // The greeting and the date follow the real clock, exactly as the design
  // does — only the compliance data is pinned to the fixture's reference day.
  const now = new Date();

  // Any missing or expired document turns the flag count red; a merely
  // expiring one leaves it amber.
  const hasRedFlag = alerts.some(
    (a) => a.status === "missing" || a.status === "expired",
  );

  return (
    <ContentColumn tight>
      <div className="mb-[13px] flex items-end justify-between gap-5">
        <div>
          <div className="mb-[5px] font-display text-[0.74rem] font-semibold tracking-[0.1em] text-brand uppercase">
            {longDateLabel(now)} · {projectName}
          </div>
          <h1 className="font-display text-[1.95rem] leading-[1.05] font-bold tracking-[0.01em] uppercase">
            {greetingFor(now)}, {firstName(siteManager.name)}
          </h1>
        </div>
      </div>

      <SiteBrainHero projectName={projectName} />

      <div className="mb-[13px] grid grid-cols-[1.1fr_1fr] gap-[18px]">
        <div className="rounded-card-lg border-[1.5px] border-border bg-surface px-6 py-[22px] shadow-card">
          <SectionLabel className="mb-4">Site at a glance</SectionLabel>
          <div className="flex items-stretch gap-5">
            <GlanceStat
              icon={Users}
              tone="text-brand"
              value={onSite.length}
              label="On site today"
            />
            <div className="w-px bg-border" />
            <GlanceStat
              icon={TriangleAlert}
              tone={hasRedFlag ? "text-danger" : "text-warn"}
              value={alerts.length}
              label="Open flags"
            />
          </div>
        </div>

        {/*
          The screen's main action, and dark for that reason (JRNL-07's
          entry-point table). Two dark cards on one screen risk competing, so
          this one is deliberately the quieter of the pair: no hazard stripe, no
          brand outline and no hero shadow — `SiteBrainHero` keeps all three and
          stays the loudest object above it.

          "End-of-day capture" went with the peach. A site day is not one thing
          that happens at 17:00, and this card is now the way a delivery at
          07:40 gets filed.
        */}
        <div className="flex flex-col rounded-card-lg border-[1.5px] border-chrome bg-chrome px-6 py-5 shadow-flat">
          <div className="mb-[7px] flex items-center gap-2 text-brand-bright">
            <span className="flex">
              <Mic size={18} />
            </span>
            <span className="font-display text-[0.68rem] font-semibold tracking-[0.1em] uppercase">
              Site capture
            </span>
          </div>
          <div className="mb-4 text-[1.18rem] font-bold text-white">
            A delivery, a hold-up, or the whole day
          </div>
          <div className="flex-1" />
          {/*
            This button was dead: it had no `onClick` at all. It opens the
            composer now.

            A pen, not a microphone: the button opens the composer, which
            offers both typing and Dictate (`product/0014`). It chose this when
            dictation did not work; it still does not start a recording itself.
          */}
          <Button
            variant="onDark"
            size="lg"
            className="w-full"
            onClick={() => setLogging(true)}
          >
            <NotebookPen size={18} />
            Log site note
          </Button>
        </div>
      </div>

      <ComplianceBanner alerts={alerts} />

      <ConnectionStatus />

      <JournalComposer
        projectId={projectId}
        date={todayIso()}
        open={logging}
        onOpenChange={setLogging}
      />
    </ContentColumn>
  );
}
